import { featureCollection } from '@turf/helpers';
import { FeatureCollection, LineString } from 'geojson';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { COLOR_MAP_CONDITION_OUTLINE } from '@alltrails/denali/tokens';
import useIsMultiSelectOverlaysEnabled from '../../hooks/useIsMultiSelectOverlaysEnabled';
import { useAppSelector } from '../../redux';
import { selectOverlayOpacity } from '../../redux/reducer';
import TrailSurfaceTypeSegment from '../../types/TrailSurfaceTypeSegment';
import { chevronsTopId, getChevronsId, getPolylineId, polylinesTopId } from '../../utils/layers';
import { decodeLatLngs, reverseCoord } from '../../utils/legacyGeoJSONConversions';
import { svgChevron } from '../../utils/svgDefinitions/svgChevron';
import useSvgImages from '../../utils/useSvgImages';
import ChevronLayer from './ChevronLayer';

const uniqueId = 'surface-type';
const sourceId = `${uniqueId}-source`;
const polylineId = getPolylineId(uniqueId);
const chevronsId = getChevronsId(uniqueId);
const chevronImages = [svgChevron(COLOR_MAP_CONDITION_OUTLINE)];

type SurfaceTypePolylineProps = {
  hideArrows?: boolean;
  segments: TrailSurfaceTypeSegment[];
};

const SurfaceTypePolyline = ({ hideArrows = false, segments }: SurfaceTypePolylineProps) => {
  const data = useMemo<FeatureCollection<LineString, { color: string }>>(() => {
    return featureCollection(
      segments.map(({ polyline, surfaceType }) => {
        const decodedPolyline = decodeLatLngs(polyline).map(latLng => reverseCoord(latLng));
        return {
          geometry: { type: 'LineString', coordinates: decodedPolyline },
          properties: { color: surfaceType.color },
          type: 'Feature'
        };
      })
    );
  }, [segments]);

  const { imagesAreLoaded } = useSvgImages(chevronImages);
  const isMultiSelectOverlaysEnabled = useIsMultiSelectOverlaysEnabled();
  const opacity = useAppSelector(state =>
    isMultiSelectOverlaysEnabled && state.map.dynamicStyleIds.includes('groundConditions') ? selectOverlayOpacity(state, 'groundConditions') : 1
  );

  return (
    <Source id={sourceId} type="geojson" data={data}>
      <Layer
        beforeId={polylinesTopId}
        id={polylineId}
        source={sourceId}
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round'
        }}
        paint={{
          'line-width': 8,
          'line-border-width': 2,
          'line-color': ['get', 'color'],
          'line-border-color': COLOR_MAP_CONDITION_OUTLINE,
          'line-opacity': opacity
        }}
      />
      {!hideArrows && imagesAreLoaded && (
        <ChevronLayer
          beforeId={chevronsTopId}
          chevronLayerId={chevronsId}
          sourceId={sourceId}
          symbolSpacing={150}
          chevronColor={COLOR_MAP_CONDITION_OUTLINE}
          opacity={opacity}
        />
      )}
    </Source>
  );
};

export default SurfaceTypePolyline;
